import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMatches, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { refreshPrincipal, switchActiveOrganization, usePrincipal } from '@/core/auth/appToken';
import { useLogout } from '@/features/auth/hooks/useLogout';
import type { ApiError } from '@/models/apiError';
import type { Principal } from '@/models/principal';

/**
 * Switches the active laboratory. Cache reset and redirect are left to {@link useResetOnLabChange},
 * which reacts to the new principal like to a lab change from another tab.
 */
export function useSwitchActiveLab(): UseMutationResult<Principal, ApiError, string> {
  const { t } = useTranslation('app');
  const logout = useLogout();

  return useMutation({
    mutationFn: switchActiveOrganization,
    onSuccess: (principal) => {
      const active = principal.selectableOrganizations.find((o) => o.active);
      toast.success(t('portal.header.activeLab.switched', { name: active?.name ?? '' }));
    },
    onError: async (err) => {
      // Same session end as a failed refresh: the backend has already dropped the cookie.
      if (err.status === 401 || err.body.code === 'ACCOUNT_DISABLED') {
        logout();
      } else if (err.body.code === 'ACTIVE_ORGANIZATION_NOT_AVAILABLE') {
        await refreshPrincipal();
        toast.error(t('portal.header.activeLab.notAvailable'));
      } else {
        toast.error(t('portal.header.activeLab.error'));
      }
    },
  });
}

/**
 * Drops all cached data of the previous lab whenever the active organization changes, also when a
 * refresh in this tab picks up a switch made in another one (shared refresh cookie). Routes marked
 * `handle.kontextgebunden` redirect to the dashboard, as their record may not exist in the new lab.
 */
export function useResetOnLabChange(): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const matches = useMatches();
  const organizationId = usePrincipal()?.organizationId ?? null;
  const previous = useRef(organizationId);

  useEffect(() => {
    const vorher = previous.current;
    previous.current = organizationId;
    if (vorher === null || organizationId === null || vorher === organizationId) return;
    const kontextgebunden = matches.some(
      (m) => (m.handle as { kontextgebunden?: boolean } | undefined)?.kontextgebunden === true,
    );
    if (kontextgebunden) {
      // Reset would refetch the detail record of the old lab before the route unmounts.
      queryClient.clear();
      navigate('/', { replace: true });
    } else {
      void queryClient.resetQueries();
    }
  }, [organizationId, matches, navigate, queryClient]);
}

/** Mount point for {@link useResetOnLabChange}; must render inside the data router. */
export function LabChangeListener(): null {
  useResetOnLabChange();
  return null;
}
